import { createContext, useContext, useMemo, useCallback, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ar from "./ar.json";
import en from "./en.json";

const dictionaries = { ar, en };
export const SUPPORTED_LANGS = ["ar", "en"];
export const DEFAULT_LANG = "ar";

const LanguageContext = createContext(null);

function getFromPath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

export function LanguageProvider({ children }) {
  const { lang: paramLang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const lang = SUPPORTED_LANGS.includes(paramLang) ? paramLang : DEFAULT_LANG;
  const dict = dictionaries[lang];

  const t = useCallback(
    (key, fallback = "") => {
      const value = getFromPath(dict, key);
      if (value === undefined || value === null) return fallback || key;
      return value;
    },
    [dict],
  );

  const switchLanguage = useCallback(
    (nextLang) => {
      if (!SUPPORTED_LANGS.includes(nextLang) || nextLang === lang) return;
      // Replace only the /:lang segment; preserve rest of the path (no scroll to top on language switch).
      const parts = location.pathname.split("/").filter(Boolean);
      if (SUPPORTED_LANGS.includes(parts[0])) {
        parts[0] = nextLang;
      } else {
        parts.unshift(nextLang);
      }
      const nextPath = "/" + parts.join("/");
      navigate(nextPath + location.search, {
        state: { skipScroll: true },
        replace: false,
      });
    },
    [lang, location.pathname, location.search, navigate],
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      const title = getFromPath(dict, "meta.title");
      const desc = getFromPath(dict, "meta.description");
      if (title) document.title = title;
      if (desc) {
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("name", "description");
          document.head.appendChild(m);
        }
        m.setAttribute("content", desc);
      }
      document.documentElement.setAttribute("lang", lang === "ar" ? "es-AR" : "en");
    }
  }, [lang, dict]);

  const value = useMemo(
    () => ({ lang, t, switchLanguage }),
    [lang, t, switchLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useI18n must be used within LanguageProvider");
  return ctx;
}
